import {
  ESTADOS_DE_PAGO,
  ETIQUETA_ESTADO_DE_PAGO,
  ETIQUETA_ESTADO_DE_PAGO_EN,
  ETIQUETA_TIPO_DE_PAGO,
  ETIQUETA_TIPO_DE_PAGO_EN,
  TIPOS_DE_PAGO,
  type CamposDePago,
  type PagoATercero,
  type TipoDePago,
} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {todosLosPagos, useCargarPagos, useCrearPago, useEditarPago, usePagosATerceros} from '../api/cashflow';
import {useFinanciadores} from '../api/financiacion';
import {et, tr} from '../i18n/idioma';
import {fecha, numero, usd} from '../utilidades/formato';
import {AYUDA_FILTROS_CASHFLOW as AF, AYUDA_FORMULARIO_PAGO as AFP, AYUDA_PAGOS} from './ayuda-cashflow';
import {NotaDeAyuda} from './nota-de-ayuda';
import {CampoDeArchivo} from './elegir-archivo';
import {FechaSegun} from './fecha-segun';
import {Paginacion} from './paginacion';
import {DEFINICION_PAGOS} from './columnas-cashflow';
import {SelectorMultiple} from './selector-multiple';
import {TablaDeCashflowComun} from './tabla-de-cashflow';
import {usePermisos} from './sesion-contexto';

const CLAVES = [
  'estado',
  'comprador',
  'proveedor',
  'tipo',
  'linea',
  'desde',
  'hasta',
  'soloVencidos',
  'buscar',
  'pagina',
  'porPagina',
  'orden',
  'direccion',
] as const;
const campo = 'mt-0.5 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta';
const boton = 'rounded-md border border-linea bg-white px-2 py-0.5 text-xs hover:bg-papel disabled:opacity-50';

// Lineas de financiacion para elegir (BLQ, LC, BC...): las que financian.
function useLineas() {
  const financiadores = useFinanciadores();
  return (financiadores.data ?? []).filter(f => f.financia);
}

// Cashflow → Pagos a terceros: lo que las empresas del grupo le pagan a los
// proveedores terceros (anticipos y liquidaciones). Se trabaja aqui: marcar
// pagado, cambiar fechas, montos y la linea con que se paga.
export function PagosATerceros() {
  const [parametros, setParametros] = useSearchParams();
  const permisos = usePermisos();
  const consulta = new URLSearchParams();
  for (const clave of CLAVES) {
    const v = parametros.get(clave);
    if (v) consulta.set(clave, v);
  }
  // Sin estado elegido se ve lo pendiente (lo pagado se ve marcandolo).
  if (!parametros.has('estado')) consulta.set('estado', 'pending|requested');
  else if (parametros.get('estado') === 'todos') consulta.delete('estado');
  const datos = usePagosATerceros(consulta.toString());
  const cambiar = (cambios: Partial<Record<(typeof CLAVES)[number], string | undefined>>) => {
    const nuevos = new URLSearchParams(parametros);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) nuevos.set(k, v);
      else if (k === 'estado') nuevos.set('estado', 'todos');
      else nuevos.delete(k);
    }
    if (!('pagina' in cambios)) nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  const [busqueda, setBusqueda] = useState(parametros.get('buscar') ?? '');
  useEffect(() => {
    const temporizador = setTimeout(() => {
      if ((parametros.get('buscar') ?? '') !== busqueda.trim()) cambiar({buscar: busqueda.trim() || undefined});
    }, 350);
    return () => clearTimeout(temporizador);
  });
  const [nuevo, setNuevo] = useState(false);
  const d = datos.data;
  const estadoElegido = parametros.get('estado') === 'todos' ? undefined : (consulta.get('estado') ?? undefined);
  return (
    <div className="space-y-3" data-prueba="cashflow-pagos">
      {permisos.editar && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setNuevo(x => !x)}
            className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro"
          >
            {tr('＋ Registrar un pago', '＋ Register a payment')}
          </button>
          <CargaDePagos />
        </div>
      )}
      {nuevo && <NuevoPago onListo={() => setNuevo(false)} />}
      <div className="grid items-end gap-2 rounded-lg border border-linea bg-white px-3 py-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <label className="block text-xs text-apagado sm:col-span-2">
          {tr('Buscar', 'Search')}
          <NotaDeAyuda texto={AF.buscarPagos} dentroDeEtiqueta />
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={tr('Exportación, proveedor, acuerdo o comentario', 'Export, supplier, agreement or comment')}
            className="mt-0.5 w-full rounded-md border border-linea px-3 py-1 text-sm text-tinta"
          />
        </label>
        <SelectorMultiple
          etiqueta={tr('Estado', 'Status')}
          ayuda={AF.estadoPagos}
          valor={estadoElegido}
          opciones={d?.opciones.estados ?? []}
          onCambiar={v => cambiar({estado: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Empresa que paga', 'Paying company')}
          ayuda={AYUDA_PAGOS.comprador}
          valor={parametros.get('comprador') ?? undefined}
          opciones={d?.opciones.compradores ?? []}
          onCambiar={v => cambiar({comprador: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Proveedor', 'Supplier')}
          ayuda={AYUDA_PAGOS.proveedor}
          valor={parametros.get('proveedor') ?? undefined}
          opciones={d?.opciones.proveedores ?? []}
          onCambiar={v => cambiar({proveedor: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Tipo', 'Type')}
          ayuda={AYUDA_PAGOS.tipo}
          valor={parametros.get('tipo') ?? undefined}
          opciones={d?.opciones.tipos ?? []}
          onCambiar={v => cambiar({tipo: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Línea', 'Line')}
          ayuda={AF.lineaPagos}
          valor={parametros.get('linea') ?? undefined}
          opciones={d?.opciones.lineas ?? []}
          onCambiar={v => cambiar({linea: v})}
        />
        <label className="flex items-center gap-2 text-xs text-apagado">
          <input
            type="checkbox"
            checked={parametros.get('soloVencidos') === 'true'}
            onChange={e => cambiar({soloVencidos: e.target.checked ? 'true' : undefined})}
          />
          {tr('Solo vencidos', 'Overdue only')}
          <NotaDeAyuda texto={AF.soloVencidos} dentroDeEtiqueta />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Fecha meta desde', 'Target date from')}
          <NotaDeAyuda texto={AF.metaDesde} dentroDeEtiqueta />
          <input
            type="date"
            value={parametros.get('desde') ?? ''}
            onChange={e => cambiar({desde: e.target.value || undefined})}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Fecha meta hasta', 'Target date to')}
          <NotaDeAyuda texto={AF.metaHasta} dentroDeEtiqueta />
          <input
            type="date"
            value={parametros.get('hasta') ?? ''}
            onChange={e => cambiar({hasta: e.target.value || undefined})}
            className={campo}
          />
        </label>
      </div>
      {datos.isError && <p className="text-sm text-alerta">{datos.error.message}</p>}
      {d && (
        <div className="flex flex-wrap gap-2 text-sm" data-prueba="totales-pagos">
          <span className="rounded-md border border-linea bg-white px-3 py-1">
            {tr('Pendiente', 'Pending')}: <b className="tabular-nums">{usd(d.totales.pendienteUsd)}</b>
          </span>
          <span
            className={`rounded-md border px-3 py-1 ${d.totales.vencidoUsd ? 'border-alerta/50 bg-red-50' : 'border-linea bg-white'}`}
          >
            {tr('Vencido', 'Overdue')}: <b className="tabular-nums">{usd(d.totales.vencidoUsd)}</b>
          </span>
          <span className="rounded-md border border-linea bg-white px-3 py-1">
            {tr('Pagado', 'Paid')}: <b className="tabular-nums">{usd(d.totales.pagadoUsd)}</b>
          </span>
          <NotaDeAyuda texto={AF.totalesPagos} />
        </div>
      )}
      <div className={datos.isFetching ? 'opacity-70' : ''}>
        <TablaDePagos
          filas={d?.elementos ?? []}
          cargando={datos.isPending}
          todasLasFilas={() => todosLosPagos(consulta.toString())}
          orden={{
            orden: parametros.get('orden') ?? '',
            direccion: parametros.get('direccion') === 'desc' ? 'desc' : 'asc',
            onOrdenar: o =>
              cambiar({
                orden: o,
                direccion: parametros.get('orden') === o && parametros.get('direccion') !== 'desc' ? 'desc' : 'asc',
              }),
          }}
        />
      </div>
      {d && (
        <Paginacion
          pagina={d.pagina}
          totalPaginas={d.totalPaginas}
          porPagina={d.porPagina}
          total={d.total}
          onPagina={p => cambiar({pagina: String(p)})}
          onPorPagina={n => cambiar({porPagina: String(n)})}
          unidad={{es: 'pagos', en: 'payments'}}
        />
      )}
    </div>
  );
}

function TablaDePagos({
  filas,
  cargando,
  orden,
  todasLasFilas,
}: {
  filas: PagoATercero[];
  cargando: boolean;
  orden: {orden: string; direccion: 'asc' | 'desc'; onOrdenar: (orden: string) => void};
  todasLasFilas: () => Promise<PagoATercero[]>;
}) {
  const permisos = usePermisos();
  const editar = useEditarPago();
  const [editando, setEditando] = useState<number | null>(null);
  const enEdicion = filas.find(x => x.id === editando);
  // "Acciones" solo para quien puede editar.
  const definicion = permisos.editar
    ? DEFINICION_PAGOS
    : {...DEFINICION_PAGOS, columnas: DEFINICION_PAGOS.columnas.filter(c => c.id !== 'acciones')};
  return (
    <>
      {editar.isError && <p className="text-sm text-alerta">{editar.error.message}</p>}
      {enEdicion && (
        <div className="rounded-lg border border-verde bg-white p-3">
          <EditorDePago key={enEdicion.id} x={enEdicion} onListo={() => setEditando(null)} />
        </div>
      )}
      <TablaDeCashflowComun
        tabla="pagos"
        hoja={tr('Pagos a terceros', 'Third-party payments')}
        todasLasFilas={todasLasFilas}
        definicion={definicion}
        filas={filas}
        claveDeFila={x => x.id}
        altoMaximo="calc(100vh - 22rem)"
        orden={orden}
        mensajeVacio={
          cargando
            ? tr('Cargando…', 'Loading…')
            : tr('No hay pagos con estos filtros.', 'No payments with these filters.')
        }
        claseDeFila={x => (x.estado === 'done' ? 'text-apagado' : undefined)}
        celda={(columna, x) => {
          const pendiente = x.estado === 'pending' || x.estado === 'requested';
          switch (columna.id) {
            case 'exportacion':
              return x.enLaApp ? (
                <Link to={`/seguimiento/${encodeURIComponent(x.exportacion)}`} className="text-verde hover:underline">
                  {x.exportacion}
                </Link>
              ) : (
                <span title={tr('Todavía no está en la app', 'Not in the app yet')}>{x.exportacion} *</span>
              );
            case 'montoUsd':
              return x.montoUsd < 0 ? (
                <span className="text-verde-oscuro" title={tr('El proveedor devuelve', 'The supplier pays back')}>
                  {columna.valor(x)}
                </span>
              ) : undefined;
            case 'fechaMeta':
              return (
                <span className={x.vencido ? 'font-medium text-alerta' : ''}>
                  {x.vencido && '⚠ '}
                  <FechaSegun
                    valor={x.fechaMeta}
                    segun={x.fechaMetaSegun}
                    regla={tr(
                      `Movida con la ETA a Mundra del seguimiento (en el archivo: ${fecha(x.fechaMetaArchivo)}).`,
                      `Moved with the Mundra ETA in the tracking (in the file: ${fecha(x.fechaMetaArchivo)}).`,
                    )}
                  />
                  {pendiente && x.diasParaMeta !== null && x.diasParaMeta >= 0 && (
                    <span className="ml-1 text-xs text-apagado">
                      ({tr(`en ${x.diasParaMeta} d`, `in ${x.diasParaMeta} d`)})
                    </span>
                  )}
                </span>
              );
            case 'linea':
              return (
                <>
                  {x.linea ?? <span className="text-apagado">—</span>}
                  {x.linea && !x.lineaDelPago && (
                    <span className="ml-1 text-xs text-apagado" title={tr('La del acuerdo', 'From the agreement')}>
                      ({tr('acuerdo', 'agreement')})
                    </span>
                  )}
                </>
              );
            case 'comentarios':
              return (
                <span className="block max-w-52 truncate text-xs" title={x.comentarios ?? undefined}>
                  {x.comentarios ?? ''}
                </span>
              );
            case 'acciones':
              return (
                <span className="whitespace-nowrap">
                  {pendiente && (
                    <button
                      type="button"
                      className={`${boton} mr-1`}
                      disabled={editar.isPending}
                      onClick={() => editar.mutate({id: x.id, edicion: {version: x.version, campos: {estado: 'done'}}})}
                    >
                      {tr('✓ Pagado', '✓ Paid')}
                    </button>
                  )}
                  <button
                    type="button"
                    className={boton}
                    aria-expanded={editando === x.id}
                    onClick={() => setEditando(editando === x.id ? null : x.id)}
                  >
                    {tr('✎ Editar', '✎ Edit')}
                  </button>
                </span>
              );
            default:
              return undefined;
          }
        }}
      />
    </>
  );
}

function EditorDePago({x, onListo}: {x: PagoATercero; onListo: () => void}) {
  const editar = useEditarPago();
  const lineas = useLineas();
  const [valores, setValores] = useState({
    fechaMeta: x.fechaMeta ?? '',
    pagadoEl: x.pagadoEl ?? '',
    estado: x.estado,
    montoUsd: String(x.montoUsd),
    linea: x.lineaDelPago ?? '',
    comentarios: x.comentarios ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const guardar = () => {
    const monto = Number(valores.montoUsd.replace(',', '.'));
    if (!Number.isFinite(monto)) return setError(tr('El monto debe ser un número.', 'The amount must be a number.'));
    const campos: CamposDePago = {
      fechaMeta: valores.fechaMeta || null,
      pagadoEl: valores.pagadoEl || null,
      estado: valores.estado,
      montoUsd: monto,
      linea: valores.linea || null,
      comentarios: valores.comentarios.trim() || null,
    };
    editar.mutate({id: x.id, edicion: {version: x.version, campos}}, {onSuccess: onListo});
  };
  const cambiar = (k: keyof typeof valores, v: string) => setValores(actual => ({...actual, [k]: v}));
  return (
    <div
      className="space-y-2"
      role="form"
      aria-label={tr(`Editar pago de ${x.exportacion}`, `Edit payment of ${x.exportacion}`)}
    >
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <label className="block text-xs text-apagado">
          {tr('Fecha meta', 'Target date')}
          <NotaDeAyuda texto={AFP.fechaMeta} dentroDeEtiqueta />
          <input
            type="date"
            value={valores.fechaMeta}
            onChange={e => cambiar('fechaMeta', e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Estado', 'Status')}
          <NotaDeAyuda texto={AFP.estado} dentroDeEtiqueta />
          <select value={valores.estado} onChange={e => cambiar('estado', e.target.value)} className={campo}>
            {ESTADOS_DE_PAGO.map(s => (
              <option key={s} value={s}>
                {et(ETIQUETA_ESTADO_DE_PAGO, ETIQUETA_ESTADO_DE_PAGO_EN)[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-apagado">
          {tr('Pagado el', 'Paid on')}
          <NotaDeAyuda texto={AFP.pagadoEl} dentroDeEtiqueta />
          <input
            type="date"
            value={valores.pagadoEl}
            onChange={e => cambiar('pagadoEl', e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Monto USD (negativo: devuelve el proveedor)', 'Amount USD (negative: supplier pays back)')}
          <NotaDeAyuda texto={AFP.monto} dentroDeEtiqueta />
          <input
            inputMode="decimal"
            value={valores.montoUsd}
            onChange={e => cambiar('montoUsd', e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Línea con que se paga', 'Line it is paid with')}
          <NotaDeAyuda texto={AFP.lineaEditar} dentroDeEtiqueta />
          <select value={valores.linea} onChange={e => cambiar('linea', e.target.value)} className={campo}>
            <option value="">{tr('La del acuerdo', 'From the agreement')}</option>
            {lineas.map(l => (
              <option key={l.codigo} value={l.codigo}>
                {l.nombre} ({l.codigo})
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-apagado lg:col-span-1">
          {tr('Comentarios', 'Comments')}
          <NotaDeAyuda texto={AFP.comentarios} dentroDeEtiqueta />
          <input value={valores.comentarios} onChange={e => cambiar('comentarios', e.target.value)} className={campo} />
        </label>
      </div>
      {(error || editar.isError) && <p className="text-sm text-alerta">{error ?? editar.error?.message}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={guardar}
          disabled={editar.isPending}
          className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {tr('Guardar', 'Save')}
        </button>
        <button type="button" onClick={onListo} className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm">
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </div>
  );
}

function NuevoPago({onListo}: {onListo: () => void}) {
  const crear = useCrearPago();
  const lineas = useLineas();
  const [v, setV] = useState({
    exportacion: '',
    tipo: 'advance' as TipoDePago,
    montoUsd: '',
    comprador: '',
    proveedor: '',
    fechaMeta: '',
    linea: '',
    comentarios: '',
  });
  const [error, setError] = useState<string | null>(null);
  const cambiar = (k: keyof typeof v, valor: string) => setV(actual => ({...actual, [k]: valor}));
  const guardar = () => {
    const monto = Number(v.montoUsd.replace(',', '.'));
    if (!v.exportacion.trim()) return setError(tr('Escribe la exportación.', 'Enter the export.'));
    if (!v.montoUsd.trim() || !Number.isFinite(monto))
      return setError(tr('El monto debe ser un número.', 'The amount must be a number.'));
    setError(null);
    crear.mutate(
      {
        exportacion: v.exportacion.trim(),
        tipo: v.tipo,
        montoUsd: monto,
        comprador: v.comprador.trim() || null,
        proveedor: v.proveedor.trim() || null,
        fechaMeta: v.fechaMeta || null,
        linea: v.linea || null,
        comentarios: v.comentarios.trim() || null,
      },
      {onSuccess: onListo},
    );
  };
  const tipos = et(ETIQUETA_TIPO_DE_PAGO, ETIQUETA_TIPO_DE_PAGO_EN);
  return (
    <div
      className="space-y-2 rounded-lg border border-linea bg-white p-4"
      role="form"
      aria-label={tr('Nuevo pago', 'New payment')}
    >
      <h2 className="font-medium">
        {tr('Registrar un pago a un proveedor tercero', 'Register a payment to a third-party supplier')}
      </h2>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block text-xs text-apagado">
          {tr('Exportación', 'Export')}
          <NotaDeAyuda texto={AFP.exportacion} dentroDeEtiqueta />
          <input value={v.exportacion} onChange={e => cambiar('exportacion', e.target.value)} className={campo} />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Tipo', 'Type')}
          <NotaDeAyuda texto={AFP.tipo} dentroDeEtiqueta />
          <select value={v.tipo} onChange={e => cambiar('tipo', e.target.value)} className={campo}>
            {TIPOS_DE_PAGO.map(t => (
              <option key={t} value={t}>
                {tipos[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-apagado">
          {tr('Monto USD', 'Amount USD')}
          <NotaDeAyuda texto={AFP.monto} dentroDeEtiqueta />
          <input
            inputMode="decimal"
            value={v.montoUsd}
            onChange={e => cambiar('montoUsd', e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Fecha meta', 'Target date')}
          <NotaDeAyuda texto={AFP.fechaMeta} dentroDeEtiqueta />
          <input
            type="date"
            value={v.fechaMeta}
            onChange={e => cambiar('fechaMeta', e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Empresa que paga', 'Paying company')}
          <NotaDeAyuda texto={AFP.comprador} dentroDeEtiqueta />
          <input value={v.comprador} onChange={e => cambiar('comprador', e.target.value)} className={campo} />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Proveedor', 'Supplier')}
          <NotaDeAyuda texto={AFP.proveedor} dentroDeEtiqueta />
          <input value={v.proveedor} onChange={e => cambiar('proveedor', e.target.value)} className={campo} />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Línea con que se paga', 'Line it is paid with')}
          <NotaDeAyuda texto={AFP.lineaNuevo} dentroDeEtiqueta />
          <select value={v.linea} onChange={e => cambiar('linea', e.target.value)} className={campo}>
            <option value="">{tr('Sin línea', 'No line')}</option>
            {lineas.map(l => (
              <option key={l.codigo} value={l.codigo}>
                {l.nombre} ({l.codigo})
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-apagado">
          {tr('Comentarios', 'Comments')}
          <NotaDeAyuda texto={AFP.comentarios} dentroDeEtiqueta />
          <input value={v.comentarios} onChange={e => cambiar('comentarios', e.target.value)} className={campo} />
        </label>
      </div>
      {(error || crear.isError) && <p className="text-sm text-alerta">{error ?? crear.error?.message}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={guardar}
          disabled={crear.isPending}
          className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {tr('Guardar pago', 'Save payment')}
        </button>
        <button type="button" onClick={onListo} className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm">
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </div>
  );
}

// Respaldo: subir el Payment Schedule Third Parties (hojas Input y Payment
// Schedule). Primero se revisa; lo editado en la app no se toca.
export function CargaDePagos({
  abiertaAlInicio = false,
  onCerrar,
}: {
  abiertaAlInicio?: boolean;
  // Abierta desde "🛠 Herramientas": al cerrarla desaparece.
  onCerrar?: () => void;
}) {
  const [abierta, setAbierta] = useState(abiertaAlInicio || !!onCerrar);
  const [archivo, setArchivo] = useState<File | null>(null);
  const cargar = useCargarPagos();
  const r = cargar.data;
  if (!abierta)
    return (
      <button
        type="button"
        onClick={() => setAbierta(true)}
        className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
      >
        {tr('⬆ Cargar Payment Schedule', '⬆ Load Payment Schedule')}
      </button>
    );
  return (
    <div className="w-full space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="carga-pagos">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">
            {tr('Cargar Payment Schedule Third Parties', 'Load Payment Schedule Third Parties')}
          </h2>
          <p className="text-xs text-apagado">
            {tr(
              'Lee solo las hojas Input (acuerdos) y Payment Schedule (pagos). Primero se revisa y nada se guarda hasta que confirmes. Lo editado en la app no se toca.',
              'Reads only the Input (agreements) and Payment Schedule (payments) sheets. It is reviewed first and nothing is saved until you confirm. What was edited in the app is not touched.',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => (onCerrar ? onCerrar() : setAbierta(false))}
          aria-label={tr('Cerrar', 'Close')}
          className="text-apagado hover:text-tinta"
        >
          ×
        </button>
      </div>
      <CampoDeArchivo
        etiqueta={tr('Payment Schedule Third Parties (.xlsx)', 'Payment Schedule Third Parties (.xlsx)')}
        accept=".xlsx"
        archivos={archivo ? [archivo] : []}
        onElegir={([a]) => {
          setArchivo(a ?? null);
          cargar.reset();
        }}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!archivo || cargar.isPending}
          onClick={() => archivo && cargar.mutate({archivo, modo: 'revisar'})}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
        >
          {tr('Revisar', 'Review')}
        </button>
        {r && !r.aplicado && (
          <button
            type="button"
            disabled={cargar.isPending}
            onClick={() => archivo && cargar.mutate({archivo, modo: 'aplicar'})}
            className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {tr('Confirmar y guardar', 'Confirm and save')}
          </button>
        )}
        {cargar.isPending && <span className="text-sm text-apagado">{tr('Leyendo…', 'Reading…')}</span>}
      </div>
      {cargar.isError && <p className="text-sm text-alerta">{cargar.error.message}</p>}
      {r && (
        <div className="space-y-1 text-sm" role="status">
          <p className="font-medium">
            {r.aplicado
              ? tr('Guardado.', 'Saved.')
              : tr('Revisión (todavía no se guardó nada):', 'Review (nothing saved yet):')}
          </p>
          <p>
            {tr(
              `${numero(r.acuerdos)} acuerdos y ${numero(r.pagos)} pagos (${numero(r.pendientes)} pendientes o solicitados); ${numero(r.editadosEnLaApp)} editados en la app no se tocan.`,
              `${numero(r.acuerdos)} agreements and ${numero(r.pagos)} payments (${numero(r.pendientes)} pending or requested); ${numero(r.editadosEnLaApp)} edited in the app are not touched.`,
            )}
          </p>
          {r.queNoEstanEnLaApp.length > 0 && (
            <p className="text-apagado">
              {tr(
                `Exportaciones que todavía no están en la app (sus pagos entran igual): ${r.queNoEstanEnLaApp.join(', ')}.`,
                `Exports not in the app yet (their payments are loaded anyway): ${r.queNoEstanEnLaApp.join(', ')}.`,
              )}
            </p>
          )}
          {r.avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {r.avisos.slice(0, 20).map(a => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
